import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { Pencil, Trash2, Plus } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authContext";

interface CartCharge {
  id: string;
  min_cart_value: string;
  max_cart_value: string;
  discount: string;
  handling_charge: string;
  platform_fee: string;
  delivery_charge: string;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

const CartCharges: React.FC = () => {
  const [cartCharges, setCartCharges] = useState<CartCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<CartCharge | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chargeToDelete, setChargeToDelete] = useState<CartCharge | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    min_cart_value: "",
    max_cart_value: "",
    discount: "",
    handling_charge: "",
    platform_fee: "",
    delivery_charge: "",
  });

  useEffect(() => {
    fetchCartCharges();
  }, []);

  const fetchCartCharges = async () => {
    try {
      const response = await api.getCartCharges();
      if (response.success) {
        setCartCharges(response.data || []);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.error || "Failed to fetch cart charges",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch cart charges",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (charge?: CartCharge) => {
    if (charge) {
      setEditingCharge(charge);
      setFormData({
        min_cart_value: charge.min_cart_value,
        max_cart_value: charge.max_cart_value,
        discount: charge.discount,
        handling_charge: charge.handling_charge,
        platform_fee: charge.platform_fee,
        delivery_charge: charge.delivery_charge,
      });
    } else {
      setEditingCharge(null);
      setFormData({
        min_cart_value: "",
        max_cart_value: "",
        discount: "",
        handling_charge: "",
        platform_fee: "",
        delivery_charge: "",
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCharge(null);
    setFormData({
      min_cart_value: "",
      max_cart_value: "",
      discount: "",
      handling_charge: "",
      platform_fee: "",
      delivery_charge: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        user_id: user?.id,
      };

      const response = editingCharge
        ? await api.updateCartCharges({ ...payload, id: editingCharge.id })
        : await api.createCartCharges(payload);

      console.log(response, "response");

      if (response.success) {
        toast({
          title: "Success",
          description: editingCharge
            ? "Cart charge updated successfully"
            : "Cart charge created successfully",
        });
        handleCloseModal();
        fetchCartCharges();
      } else {
        toast({
          variant: "destructive",
          title: `Error updating cart charge ${response.code}`,
          description: response.error || "Failed to save cart charge",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save cart charge",
      });
    }
  };

  const handleDelete = async () => {
    if (!chargeToDelete) return;

    try {
      const response = await api.deleteCartCharges(Number(chargeToDelete.id));

      if (response.success) {
        toast({
          title: "Success",
          description: "Cart charge deleted successfully",
        });
        setDeleteConfirmOpen(false);
        setChargeToDelete(null);
        fetchCartCharges();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.error || "Failed to delete cart charge",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete cart charge",
      });
    }
  };

  const formatCurrency = (value: string | number) => {
    return `₹${Number(value || 0).toFixed(2)}`;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 min-h-screen bg-white dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Cart Charges</h1>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="mr-2 h-4 w-4" /> Add Cart Charge
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Min Cart Value</TableHead>
            <TableHead>Max Cart Value</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Packing & Handling Charge</TableHead>
            <TableHead>Platform Fee</TableHead>
            <TableHead>Delivery Charge</TableHead>
            <TableHead>Updated At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cartCharges.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                No cart charges found
              </TableCell>
            </TableRow>
          ) : (
            cartCharges.map(charge => (
              <TableRow key={charge.id}>
              <TableCell>
                {charge.max_cart_value === null || charge.max_cart_value === "0"
                  ? `₹${Number(charge.min_cart_value)}>`
                  : formatCurrency(charge.min_cart_value)}
              </TableCell>
                <TableCell>
                  {charge.max_cart_value === null || charge.max_cart_value === "0"
                    ? ""
                    : formatCurrency(charge.max_cart_value)}
                </TableCell>
                <TableCell>{formatCurrency(charge.discount)}</TableCell>
                <TableCell>{formatCurrency(charge.handling_charge)}</TableCell>
                <TableCell>{formatCurrency(charge.platform_fee)}</TableCell>
                <TableCell>{formatCurrency(charge.delivery_charge)}</TableCell>
                <TableCell>
                  {new Date(charge.updated_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenModal(charge)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setChargeToDelete(charge);
                        setDeleteConfirmOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingCharge ? "Edit Cart Charge" : "Add Cart Charge"}
            </DialogTitle>
            <DialogDescription>
              Set the cart value range and associated charges. Ranges cannot
              overlap.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="min_cart_value">Min Cart Value (₹)</Label>
                  <Input
                    id="min_cart_value"
                    type="number"
                    step="0.01"
                    value={formData.min_cart_value}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        min_cart_value: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="max_cart_value">Max Cart Value (₹)</Label>
                  <Input
                    id="max_cart_value"
                    type="number"
                    step="0.01"
                    value={formData.max_cart_value}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        max_cart_value: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="discount">Discount (₹)</Label>
                <Input
                  id="discount"
                  type="number"
                  step="0.01"
                  value={formData.discount}
                  onChange={e =>
                    setFormData({ ...formData, discount: e.target.value })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="handling_charge">Handling Charge (₹)</Label>
                <Input
                  id="handling_charge"
                  type="number"
                  step="0.01"
                  value={formData.handling_charge}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      handling_charge: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="platform_fee">Platform Fee (₹)</Label>
                <Input
                  id="platform_fee"
                  type="number"
                  step="0.01"
                  value={formData.platform_fee}
                  onChange={e =>
                    setFormData({ ...formData, platform_fee: e.target.value })
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="delivery_charge">Delivery Charge (₹)</Label>
                <Input
                  id="delivery_charge"
                  type="number"
                  step="0.01"
                  value={formData.delivery_charge}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      delivery_charge: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingCharge ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this cart charge? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setChargeToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CartCharges;
